import { FC, MouseEvent, useEffect, useMemo } from "react";
import styled from "@emotion/styled";
import { useQuery } from "convex/react";
import { observer } from "mobx-react-lite";
import { api } from "@/smart-views/client/api";
import { setSmartViewTitle } from "@/smart-views/client/title-cache";
import { materializeSmartView } from "@/smart-views/client/backend-api";
import { SMART_VIEW_ICON_KIND } from "@/smart-views/icons";
import { useAppStore } from "@/store";
import { logger } from "@/modules/logger";
import { MDSIcon, MDSIconColor, MDSIconKind } from "@/mds/components/icon";
import type { ChipToken } from "./parse-chips";

interface LinkedObjectChipProps {
  chip: ChipToken;
  // When true, chip is rendered inline within prose (markdown). Otherwise it's
  // standalone (table cell, kv value, etc). Affects spacing, not behavior.
  inline?: boolean;
}

export const LinkedObjectChip: FC<LinkedObjectChipProps> = observer(({ chip, inline = false }) => {
  if (chip.kind === "smart-view") {
    return <SmartViewChip smartViewId={chip.id} inline={inline} />;
  }
  // Note chips are stubbed for v1 — render a non-interactive label so the agent
  // can already emit them and we light up the click path in the next pass.
  return (
    <NoteChip $inline={inline} title="Note links not yet routable">
      <MDSIcon kind={MDSIconKind.Notebook} size={12} boxSize={12} color={MDSIconColor.Secondary} />
      <span>Note</span>
    </NoteChip>
  );
});

const SmartViewChip: FC<{ smartViewId: string; inline: boolean }> = observer(
  ({ smartViewId, inline }) => {
    const { store } = useAppStore();

    const result = useQuery(api.smartViews.client.getSmartViewChipMetadata, {
      smartViewIds: [smartViewId],
    });
    const metadata = result?.[0];

    // If the target redirects, transparently fetch the canonical row's metadata
    // for title display. Navigation also follows the redirect.
    const redirectedResult = useQuery(
      api.smartViews.client.getSmartViewChipMetadata,
      metadata !== undefined && metadata.exists && metadata.redirectTo !== undefined
        ? { smartViewIds: [metadata.redirectTo] }
        : "skip"
    );
    const redirectedMetadata = redirectedResult?.[0];

    const effective = useMemo(() => {
      if (metadata === undefined) return undefined;
      if (!metadata.exists) return { kind: "missing" as const };
      if (metadata.redirectTo !== undefined) {
        if (redirectedMetadata === undefined) {
          return { kind: "loading" as const, title: metadata.title };
        }
        if (!redirectedMetadata.exists) return { kind: "missing" as const };
        return {
          kind: "real" as const,
          smartViewId: metadata.redirectTo,
          title: redirectedMetadata.title,
          isJit: redirectedMetadata.status === "jit",
        };
      }
      return {
        kind: "real" as const,
        smartViewId,
        title: metadata.title,
        isJit: metadata.status === "jit",
      };
    }, [metadata, redirectedMetadata, smartViewId]);

    // Keep the title cache populated so tab titles work immediately when the
    // user clicks through to a JIT or chip-only smart view.
    useEffect(() => {
      if (effective?.kind === "real") {
        setSmartViewTitle(effective.smartViewId, effective.title);
      }
    }, [effective]);

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (effective === undefined || effective.kind !== "real") return;

      const targetId = effective.smartViewId;
      // Navigate first so the user sees the page transition immediately —
      // even if the materialize call is slow, the destination's status flip
      // will stream in via Convex reactivity.
      store.navigation.goToSmartView({ smartViewId: targetId });

      if (effective.isJit) {
        materializeSmartView({ smartViewId: targetId }).catch(err => {
          logger.error({
            message: "Failed to materialize JIT smart view",
            info: { smartViewId: targetId, err: String(err) },
          });
        });
      }
    };

    if (effective === undefined) {
      return (
        <ChipShell $inline={inline} $variant="loading" as="span">
          <MDSIcon
            kind={SMART_VIEW_ICON_KIND}
            size={12}
            boxSize={12}
            color={MDSIconColor.Secondary}
          />
          <ChipLabel>Loading…</ChipLabel>
        </ChipShell>
      );
    }

    if (effective.kind === "missing") {
      return (
        <ChipShell $inline={inline} $variant="missing" as="span">
          <MDSIcon
            kind={SMART_VIEW_ICON_KIND}
            size={12}
            boxSize={12}
            color={MDSIconColor.Secondary}
          />
          <ChipLabel>Unknown smart view</ChipLabel>
        </ChipShell>
      );
    }

    if (effective.kind === "loading") {
      return (
        <ChipShell $inline={inline} $variant="loading" as="span">
          <MDSIcon
            kind={SMART_VIEW_ICON_KIND}
            size={12}
            boxSize={12}
            color={MDSIconColor.Secondary}
          />
          <ChipLabel>{effective.title}</ChipLabel>
        </ChipShell>
      );
    }

    return (
      <ChipShell
        $inline={inline}
        $variant={effective.isJit ? "jit" : "real"}
        href="#"
        onClick={handleClick}
        title={effective.isJit ? "Tap to create" : effective.title}
      >
        <MDSIcon
          kind={SMART_VIEW_ICON_KIND}
          size={12}
          boxSize={12}
          color={MDSIconColor.Secondary}
        />
        <ChipLabel>{effective.title}</ChipLabel>
      </ChipShell>
    );
  }
);

type ChipVariant = "real" | "jit" | "loading" | "missing";

const ChipShell = styled.a<{ $inline: boolean; $variant: ChipVariant }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 6px;
  border-radius: 6px;
  font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
  line-height: ${({ theme }) => theme.lineHeights["text-xs"]};
  color: ${({ theme }) => theme.colors.text.primary};
  text-decoration: none;
  vertical-align: ${({ $inline }) => ($inline ? "baseline" : "middle")};
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  cursor: ${({ $variant }) => ($variant === "real" || $variant === "jit" ? "pointer" : "default")};
  user-select: none;

  background-color: ${({ theme, $variant }) =>
    $variant === "missing" ? "transparent" : theme.colors.background.button.chip.default};
  border: 1px ${({ $variant }) => ($variant === "jit" ? "dashed" : "solid")}
    ${({ theme, $variant }) =>
      $variant === "jit"
        ? theme.colors.border.default
        : $variant === "missing"
          ? theme.colors.border.default
          : "transparent"};

  &:hover {
    background-color: ${({ theme, $variant }) =>
      $variant === "real" || $variant === "jit"
        ? theme.colors.background.button.chip.hover
        : undefined};
  }
`;

const ChipLabel = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  max-width: 200px;
`;

const NoteChip = styled.span<{ $inline: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 6px;
  border-radius: 6px;
  background-color: ${({ theme }) => theme.colors.background.button.chip.default};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
  line-height: ${({ theme }) => theme.lineHeights["text-xs"]};
  vertical-align: ${({ $inline }) => ($inline ? "baseline" : "middle")};
`;
